import { useEffect, useRef, useState } from "react"
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"

import { isAuthenticated } from "@/lib/auth"
import { useMediaQuery } from "@/hooks/use-media-query"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Icons } from "@/components/icons"
import { useTheme } from "@/components/providers/theme-provider"

export const Route = createFileRoute("/_auth")({
  beforeLoad: async ({ location }) => {
    const authenticated = await isAuthenticated()

    if (authenticated) {
      throw redirect({
        to: "/",
        search: {
          redirect: location.href,
        },
      })
    }
  },
  component: AuthLayout,
})

function AuthLayout() {
  const { theme, setTheme } = useTheme()
  const [mode, setMode] = useState<string>("")
  const themeStoredInLocalStorage =
    localStorage.getItem("vite-ui-theme") || undefined

  const systemIntegrationDescription = `Our billing system is built for seamless integration, consolidating consumer records, meter data, payment transactions, and policy-based billing into a unified platform. It ensures real-time synchronization between meter readings, invoicing, and payment processing, reducing manual workload and improving accuracy. With automated notifications and configurable rate structures, it adapts effortlessly to your billing operations. `
  const advancedAlgorithmDescription = `Leveraging intelligent analytics, our system detects usage patterns and automates payment status updates to streamline the billing process. Equipped with intelligent automation for reminders, it ensures timely notifications for due payments, disconnections, and reconnections. The system dynamically applies zone-based rates, automates billing calculations, and optimizes financial tracking, enhancing overall revenue management and stability. It intelligently calculates due amounts based on consumption, factoring in previous balances, applicable rate structures, and necessary adjustments such as overcharges or billing corrections. This ensures accurate invoicing, minimizes discrepancies, and provides a seamless and transparent billing experience.`

  // const shortSystemIntegrationDescription =
  //   systemIntegrationDescription.slice(0, 145) + "."
  // const shortAdvancedAlgorithmDescription =
  //   advancedAlgorithmDescription.slice(0, 90) + "."

  // const [systemExpanded, setSystemExpanded] = useState(true)
  // const [algorithmExpanded, setAlgorithmExpanded] = useState(true)

  const [shouldScroll, setShouldScroll] = useState(false)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const isSmallMediumScreen = useMediaQuery("(max-width: 1024px)")

  useEffect(() => {
    const checkCompression = () => {
      const isZoomed = window.devicePixelRatio >= 1.1
      if (dialogRef.current) {
        const dialogHeight = dialogRef.current.clientHeight
        setShouldScroll(isZoomed || dialogHeight >= 600)
      }
    }

    checkCompression()
    window.addEventListener("resize", checkCompression)
    return () => window.removeEventListener("resize", checkCompression)
  }, [])

  return (
    <>
      <div className="relative flex h-screen w-screen grid-cols-3 flex-col items-center justify-center overflow-hidden md:grid">
        <DropdownMenu>
          <DropdownMenuTrigger className="absolute right-3 top-5 z-10 cursor-pointer rounded-full border border-transparent bg-slate-50 p-1 text-primary">
            <Icons.moon />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("light")}>
              Light
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {themeStoredInLocalStorage == "dark" ? (
          <Icons.bubble2Dark className="absolute left-[50%] top-[0%] md:left-[75%] md:block" />
        ) : (
          <Icons.bubble2 className="absolute left-[50%] top-[0%] md:left-[75%] md:block" />
        )}
        {themeStoredInLocalStorage == "dark" ? (
          <Icons.bubble3Dark className="absolute bottom-[0%] left-[45%] hidden h-[120px] md:block" />
        ) : (
          <Icons.bubble3 className="absolute bottom-[0%] left-[45%] hidden h-[120px] md:block" />
        )}
        {themeStoredInLocalStorage == "dark" ? (
          <Icons.bubble1Dark className="absolute bottom-[-5%] left-[-20%] hidden h-[70%] w-[55%] md:right-[50%] md:block" />
        ) : (
          <Icons.bubble1 className="absolute bottom-[-5%] left-[-20%] hidden h-[70%] w-[55%] md:right-[50%] md:block" />
        )}

        {themeStoredInLocalStorage == "dark" ? (
          <Icons.ellipseDark className="absolute left-[2%] top-[20%] hidden h-[50px] md:block" />
        ) : (
          <Icons.ellipse className="absolute left-[2%] top-[20%] hidden h-[50px] md:block" />
        )}
        {themeStoredInLocalStorage == "dark" ? (
          <Icons.ellipseDark className="absolute left-[39%] top-[73%] hidden h-[40px] md:block" />
        ) : (
          <Icons.ellipse className="absolute left-[39%] top-[73%] hidden h-[40px] md:block" />
        )}
        <div className="relative hidden flex-col p-4 *:z-10 sm:col-span-2 md:flex lg:pb-10 lg:pl-36">
          <div className="md:hidden lg:block">
            <h1 className="font-gilroy-bold text-[80px] text-primary">
              Billed intelligently.
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Card className="shadow-inner-custom rounded-2xl bg-[#00b7ae] shadow-lg md:w-[50%] lg:w-[400px]">
              <CardHeader className="space-y-2">
                <Icons.cardloginicon height="50px" width="50px" />
                <p className="font-gilroy-bold text-lg text-white md:text-3xl">
                  System <span className="block">Integration</span>
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-9">
                <CardDescription className="text-md font-gilroy-semibold line-clamp-5 text-white">
                  {systemIntegrationDescription}
                </CardDescription>
                <CardFooter className="p-0">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-white text-[#03A8A2] hover:bg-white dark:hover:bg-foreground dark:hover:text-primary">
                        Read More
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl border-primary bg-[#00b7ae] shadow-inner sm:max-w-[425px] lg:max-h-[400px] lg:max-w-[600px]">
                      <DialogHeader className="space-y-4">
                        <Icons.cardloginicon height="50px" width="50px" />
                        <DialogTitle className="font-gilroy-bold text-lg text-white md:text-3xl">
                          System <span className="block">Integration</span>
                        </DialogTitle>
                        <ScrollArea className="h-72 w-full rounded-md pr-3">
                          <DialogDescription className="font-gilroy-semibold text-pretty text-lg leading-relaxed tracking-tight text-white">
                            {systemIntegrationDescription}
                          </DialogDescription>
                        </ScrollArea>
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </CardFooter>
              </CardContent>
            </Card>
            <Card className="shadow-inner-custom rounded-2xl bg-transparent shadow-inner backdrop-blur-sm md:w-[50%] lg:w-[400px]">
              <CardHeader className="space-y-3">
                <Icons.cardloginicon2 height="50px" width="50px" />
                <p className="font-gilroy-bold text-lg text-[#00b7ae] md:text-3xl">
                  Advanced <span className="block">Algorithm</span>
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-9">
                <CardDescription className="text-md font-gilroy-semibold line-clamp-5 text-[#5a5a5a] dark:text-[#00b7ae]">
                  {advancedAlgorithmDescription}
                </CardDescription>
                <CardFooter className="p-0">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="dark:hover:bg-primary dark:hover:text-white">
                        Read More
                      </Button>
                    </DialogTrigger>
                    <DialogContent
                      ref={dialogRef}
                      className="rounded-2xl from-[#3f3d51] sm:max-w-[425px] lg:max-h-[600px] lg:max-w-[600px]"
                    >
                      <DialogHeader className="space-y-4">
                        <Icons.cardloginicon2 height="50px" width="50px" />
                        <DialogTitle className="font-gilroy-bold text-lg text-[#00b7ae] md:text-3xl">
                          Advanced <span className="block">Algorithm</span>
                        </DialogTitle>

                        {shouldScroll || isSmallMediumScreen ? (
                          <ScrollArea className="h-72 w-full rounded-md pr-3">
                            <DialogDescription className="font-gilroy-semibold text-pretty text-lg leading-relaxed tracking-tight text-black dark:text-[#00b7ae]">
                              {advancedAlgorithmDescription}
                            </DialogDescription>
                          </ScrollArea>
                        ) : (
                          <DialogDescription className="font-gilroy-semibold text-pretty text-lg leading-relaxed tracking-tight text-black dark:text-[#00b7ae]">
                            {advancedAlgorithmDescription}
                          </DialogDescription>
                        )}
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </CardFooter>
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="z-20 flex flex-col items-center md:mr-10 md:justify-center md:p-2 lg:mr-0">
          <div>
            <Outlet />
          </div>
        </div>
        {themeStoredInLocalStorage == "dark" ? (
          <Icons.bubble2Dark className="absolute bottom-[-20%] left-[0%] block rotate-180 md:right-[50%] md:hidden" />
        ) : (
          <Icons.bubble2 className="absolute bottom-[-20%] left-[0%] block rotate-180 md:right-[50%] md:hidden" />
        )}
      </div>
    </>
  )
}
